<template>
  <panel-main ref="panelMainRef">
    <!-- 表格区域 -->
    <div class="table-content">
      <!-- 表格 -->
      <ml-table
        ref="tableRef"
        :tableData="tableData"
        :templateData="templateData"
        :pageParams="pageParams"
        tableHeight="100%"
        :useJumpClick="false"
      >
        <template #versionCode="{ row }">
          {{ row.versionCode ? "v" + row.versionCode : "-" }}
        </template>

        <template #upgradeType="{ row }">
          {{ $getOptsItem(row.upgradeType, versionType) }}
        </template>

        <template #btnActive="{ row }">
          <auth-button
            type="primary"
            size="small"
            @handleClick="downloadUpgradationFile(row)"
            >下载升级文件包</auth-button
          >
          <auth-button
            type="warning"
            size="small"
            @handleClick="openVersionNoteDialog(row)"
            >修改备注说明</auth-button
          >
        </template>
      </ml-table>
    </div>
  </panel-main>

  <!-- 上传版本 弹窗 -->
  <tool-dialog
    ref="versionDialogRef"
    title="上传新版本"
    @confirm="versionSubmitForm"
  >
    <ml-form
      ref="bucketFormRef"
      label-position="top"
      className="hide-scrollbar file-type-form"
      :model="versionFormData"
      :rules="bucketFormRules"
    >
      <el-form-item label="版本号：" prop="versionCode">
        <ml-input
          placeholder="请输入版本号"
          v-model="versionFormData.versionCode"
        >
          <template #prepend>
            <span>v</span>
          </template>
        </ml-input>
      </el-form-item>
      <el-form-item label="版本类型：" prop="upgradeType">
        <el-radio-group v-model="versionFormData.upgradeType">
          <el-radio
            :label="item.value"
            size="large"
            v-for="(item, index) in versionType"
            :key="'item' + index"
            >{{ item.label }}</el-radio
          >
        </el-radio-group>
      </el-form-item>
      <el-form-item label="安装包文件：" prop="upgradeUrl">
        <div class="upload-files-box">
          <upload-file
            uploadText="安装包文件上传"
            v-model:fileList="fileList"
            folder="DEVICE_FIRMWARE"
            :uploadInterceptCall="uploadInterceptCall"
            accept=".bin"
            :disabled="!testVersionCode"
          ></upload-file>
          <div class="tips show-line-tips" v-if="!testVersionCode">
            需要先进行版本号的填写
          </div>
          <div class="tips show-line-tips" v-else>
            {{
              `温馨提示：请上传Bin格式的文件，正确命名安装包名称，如：${
                modelVersion ? modelVersion : "GBMS000000v0.0.1"
              }.bin，若核对不成功将无法上传安装包`
            }}
          </div>
        </div>
      </el-form-item>
      <el-form-item
        label="备注："
        prop="versionNote"
        class="storage-capacity-item"
      >
        <ml-input
          type="textarea"
          placeholder="请输入备注"
          v-model="versionFormData.versionNote"
        ></ml-input>
      </el-form-item>
    </ml-form>
  </tool-dialog>

  <!-- 修改备注说明 弹窗 -->
  <tool-dialog
    ref="versionNoteDialogRef"
    title="修改备注说明"
    width="500px"
    @confirm="versionNoteSubmitForm"
  >
    <ml-form
      ref="versionNoteFormRef"
      label-position="top"
      className="hide-scrollbar file-type-form"
      :model="versionNoteFormData"
    >
      <el-form-item
        label="备注："
        prop="versionNote"
        class="storage-capacity-item"
      >
        <ml-input
          type="textarea"
          :rows="6"
          placeholder="请输入备注"
          v-model="versionNoteFormData.versionNote"
        ></ml-input>
      </el-form-item>
    </ml-form>
  </tool-dialog>
</template>

<script setup>
/* 文件上传 */
import uploadFile from "@/components/upload-file.vue";
import {
  ref,
  onMounted,
  reactive,
  getCurrentInstance,
  computed,
  nextTick,
  watch,
} from "vue";

import { useTableParamsModule, usePageModule } from "@/utils/mixin";
import { versionOpts } from "@/utils/options";
import { versionNumberRul } from "@/utils/formRules";
import { versionNumber } from "@/utils/regExp";

const { routeParams } = usePageModule();

const { proxy } = getCurrentInstance();

const props = defineProps({
  tabValue: {
    type: [String, Number],
    default: "",
  },
});

// 设备id
const deviceId = computed(() => routeParams.deviceId);
// 设备型号
const versionModel = computed(() => routeParams.model);

/* 选型数据 */
// 版本类型
const versionType = versionOpts();

// 面板
// ref
const panelMainRef = ref();

// 表格配置
const {
  resetParams,
  tableData,
  pageParams,
  fetchFilterData,
  getTableList,
  openTableLoading,
  tableRef,
  filterParams,
} = useTableParamsModule({
  fetchTableName: "fetchGetDeviceFirmwarePageList",
});

// 表格模板数据
const templateData = [
  {
    type: "index",
    label: "序号",
    width: "120",
    align: "left",
  },
  {
    prop: "model",
    label: "设备型号",
    minWidth: "170",
    align: "left",
  },
  {
    prop: "versionCode",
    label: "版本号",
    showTemplate: true,
    minWidth: "170",
    align: "left",
  },
  {
    prop: "upgradeType",
    label: "版本类型",
    showTemplate: true,
    minWidth: "170",
    align: "left",
  },
  {
    prop: "upgradeTime",
    label: "上传时间",
    minWidth: "200",
    align: "left",
  },
  {
    prop: "versionNote",
    label: "备注",
    minWidth: "240",
    align: "left",
  },
  {
    prop: "btnActive",
    label: "操作",
    showTemplate: true,
    align: "left",
    fixed: "right",
    minWidth: "240",
  },
];

// *********************************************************************************************************
// 表格数据操作  START
// *********************************************************************************************************
/* 下载升级包文件 */
const downloadUpgradationFile = async (data) => {
  const { upgradeUrl, model, versionCode, id } = data;
  const urls = upgradeUrl ? upgradeUrl.split("/") : [];
  const url = urls[urls.length - 1];

  try {
    const query = proxy.$paramToStr({
      firmwareName: url,
      id,
    });
    const result = await proxy.$storeDispatch("fetchDownloadFirmware", query);
    proxy.$downloadFileBlob(result, `${model}v${versionCode}.bin`);
  } catch (error) {
    console.log(error);
  }
};

/* 上传版本弹窗 */
// ref
const versionDialogRef = ref();
const bucketFormRef = ref();

// 表单信息
const versionFormDataDefault = () => ({
  deviceId: "",
  upgradeUrl: "",
  versionCode: "",
  upgradeType: 1,
  versionNote: "",
});
const versionFormData = reactive(versionFormDataDefault());

const bucketFormRules = computed(() => {
  const rules = {
    upgradeUrl: [
      {
        required: true,
        trigger: "change",
        message: "请上传安装包",
      },
    ],
    upgradeType: [
      {
        required: true,
        trigger: "change",
        message: "请选择版本类型",
      },
    ],
    versionCode: versionNumberRul(),
  };

  return rules;
});

/* 上传的文件 */
const fileList = ref([]);
watch(
  () => fileList.value,
  (nVal) => {
    const [fileItem = {}] = nVal;
    versionFormData.upgradeUrl = fileItem.relUrl;
  },
  {
    deep: true,
  }
);

/* 判断型号与上传文件包名是否一致 */
const uploadInterceptCall = (files) => {
  // 判断文件名
  const { name } = files;
  // 分割 名称与后缀（兼容大小写 .bin 或 .BIN）
  const nameAfters = name.split(/\.[bB][iI][nN]/)[0];
  // 将文件名中的 V 或 v 统一转为小写进行比较
  const normalizedFileName = nameAfters.toLowerCase();
  const normalizedModelVersion = modelVersion.value ? modelVersion.value.toLowerCase() : '';
  const UpdateFileName = normalizedModelVersion === normalizedFileName;
  
  if (UpdateFileName) {
    return !UpdateFileName;
  } else {
    nextTick(() => {
      fileList.value = [];
    });
    proxy.$warningMsg(
      "请正确命名安装包名称，如：GBMS004E01(设备型号) + v1.45（版本号）.bin"
    );
    return !UpdateFileName;
  }
};

// 拼接型号和版本
const modelVersion = computed(() => {
  const model = versionModel.value;

  const { versionCode } = versionFormData;
  if (model && versionCode) {
    return model + "v" + versionCode;
  }
});

// 判断版本号是否符合标准
const testVersionCode = computed(() => {
  const { versionCode } = versionFormData;
  const version = versionNumber(versionCode);
  return version;
});

// 选中的数据
const chooseItem = reactive({});

// 打开弹窗
const openVersionDialog = (data = {}) => {
  // 赋值
  // Object.assign(chooseItem, { ...data });

  versionDialogRef.value.openDialog();

  nextTick(async () => {
    proxy.$updateParams(versionFormData, versionFormDataDefault());
    fileList.value = [];
    setTimeout(() => {
      bucketFormRef.value.clearValidate();
    });
  });
};

/* 修改备注弹窗 */
// ref
const versionNoteDialogRef = ref();
const versionNoteFormRef = ref();

// 表单信息
const versionNoteFormDataDefault = () => ({
  versionNote: "",
  id: "",
});
const versionNoteFormData = reactive(versionNoteFormDataDefault());

// 打开修改备注弹窗
const openVersionNoteDialog = (data = {}) => {
  // 赋值
  Object.assign(chooseItem, { ...data });

  versionNoteDialogRef.value.openDialog();

  nextTick(async () => {
    proxy.$updateParams(versionNoteFormData, data);
  });
};

// 上传版本表单提交
const versionSubmitForm = async (closeLoading) => {
  const testRes = await proxy.$testForm(closeLoading, bucketFormRef.value);
  if (!testRes) {
    return;
  }
  versionFormData.deviceId = deviceId.value;
  let result = proxy.$removeDecontextAttr(versionFormData);
  // console.log(result);
  try {
    await proxy.$storeDispatch("fetchUploadFirmware", result);
    proxy.$successMsg("上传成功");
    closeLoading();
    versionDialogRef.value.closeDialog();
    getTableList();
  } catch (error) {
    closeLoading();
  }
};

// 修改备注表单提交
const versionNoteSubmitForm = async (closeLoading) => {
  // const testRes = await proxy.$testForm(closeLoading, bucketFormRef.value);
  // if (!testRes) {
  //   return;
  // }
  let result = proxy.$removeDecontextAttr(versionNoteFormData);
  // console.log(result);
  try {
    await proxy.$storeDispatch("fetchModifyDeviceFirmwareNote", result);
    proxy.$successMsg("修改成功");
    closeLoading();
    versionNoteDialogRef.value.closeDialog();
    getTableList();
  } catch (error) {
    closeLoading();
  }
};

// 删除设备固件
const handleDeleteFirmware = async (row) => {
  try {
    await proxy.$confirm(`确定要删除版本号为v${row.versionCode}的设备固件吗？此操作不可恢复。`, '警告', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });
    
    await proxy.$storeDispatch("fetchDeleteFirmware", { id: row.id });
    
    proxy.$successMsg('删除成功');
    getTableList();
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error);
      proxy.$errorMsg('删除失败');
    }
  }
};

// 获取列表第一条数据
const getFirstRowData = () => {
  if (tableData.value && tableData.value.result && tableData.value.result.length > 0) {
    return tableData.value.result[0];
  }
  return null;
};

// *********************************************************************************************************
// 表格数据操作  END
// *********************************************************************************************************

// *********************************************************************************************************
// 查询 START
// *********************************************************************************************************

// *********************************************************************************************************
// 查询 END
// *********************************************************************************************************

defineExpose({
  resetParams,
  fetchFilterData,
  getTableList,
  openTableLoading,
  openVersionDialog,
  handleDeleteFirmware,
  getFirstRowData,
});
</script>

<style lang="scss" scoped>
.table-content {
  height: 100%;
}

.byte-group {
  width: 100%;
  display: flex;
  margin-top: 15px;
  position: relative;
  margin-bottom: 10px;
  .item {
    padding-left: 12px;

    &:first-child {
      padding-left: 0;
    }
  }
}
</style>

<style lang="scss">
.file-type-form {
  .storage-capacity-item {
    margin-bottom: 27px;
  }

  .tips {
    color: #f59a23;
  }
}
</style>
